import type {
  ListSystemSettingsResponseDto,
  SystemSettingHistoryResponseDto,
  SystemSettingResponseDto,
  SystemSettingResponseDtoValue,
} from '@evimo/client/models';
import { ResponseError } from '@evimo/client/runtime';
import {
  platformSettingsApi,
  platformSettingsRequestInit,
} from './platform-settings-api-instance';

export class PlatformSettingsApiError extends Error {
  public constructor(public readonly status?: number) {
    super('Platform settings request failed');
    this.name = 'PlatformSettingsApiError';
  }
}

async function invokePlatformSettingsClient<T>(
  action: () => Promise<T>,
): Promise<T> {
  try {
    return await action();
  } catch (error) {
    if (error instanceof ResponseError) {
      throw new PlatformSettingsApiError(error.response.status);
    }
    throw new PlatformSettingsApiError();
  }
}

export const PlatformSettingsService = {
  list(): Promise<ListSystemSettingsResponseDto> {
    return invokePlatformSettingsClient(() =>
      platformSettingsApi.systemSettingsControllerList(
        platformSettingsRequestInit,
      ),
    );
  },

  get(key: string): Promise<SystemSettingResponseDto> {
    return invokePlatformSettingsClient(() =>
      platformSettingsApi.systemSettingsControllerGet(
        { key },
        platformSettingsRequestInit,
      ),
    );
  },

  history(
    key: string,
    options: { page?: number; limit?: number } = {},
  ): Promise<SystemSettingHistoryResponseDto> {
    return invokePlatformSettingsClient(() =>
      platformSettingsApi.systemSettingsControllerHistory(
        {
          key,
          ...(options.page !== undefined ? { page: options.page } : {}),
          ...(options.limit !== undefined ? { limit: options.limit } : {}),
        },
        platformSettingsRequestInit,
      ),
    );
  },

  update(
    key: string,
    payload: {
      value: SystemSettingResponseDtoValue;
      expectedVersion: number;
      reason?: string;
    },
  ): Promise<SystemSettingResponseDto> {
    return invokePlatformSettingsClient(() =>
      platformSettingsApi.systemSettingsControllerUpdate(
        { key, updateSystemSettingRequestDto: payload },
        platformSettingsRequestInit,
      ),
    );
  },
};
